import { animate, style, transition, trigger } from '@angular/animations';
import { HttpStatusCode } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import _ from 'lodash';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import {
    FilterField,
    FilterSurveyJobChange,
    SurveyFilterValues
} from 'src/app/feature/map-viewer/map-menus/shared/job-filter/job-filter.types';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { ActionItem } from 'src/app/shared/common/components/panel-header/panel-header.component';
import { SortChangeEvent, SortMenuItem } from 'src/app/shared/common/components/sort-menu/sort-menu.component';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { SortDirection } from 'src/app/shared/common/enums/sql-operators/sort-direction.enum';
import {
    JobFilterStreamsService,
    statusDisplayNameToValueMap
} from 'src/app/shared/common/job-filter/job-filter-streams.service';
import { MenuService } from 'src/app/shared/common/layout/menu.service';
import { ItemSortMode, ItemSortService } from 'src/app/shared/common/services/item-sort.service';
import { PanelMenuService } from 'src/app/shared/common/services/panel-menu/panel-menu.service';
import { StringUtils } from 'src/app/shared/common/utility/string-utils';
import {
    JobListSupportedFields,
    SurveyJob,
    SurveyJobFilterId,
    SurveyJobListOptions,
    SurveyJobStatus
} from 'src/app/shared/survey-data-services/survey-job';
import { SurveyJobPreloadService } from 'src/app/shared/survey-data-services/survey-job-preload.service';
import { SurveyJobStreamsService } from 'src/app/shared/survey-data-services/survey-job-streams.service';
import { SurveyJobService } from 'src/app/shared/survey-data-services/survey-job.services';
import { User } from 'src/app/shared/user/user';
import { UserMapWorkspaceSurveyFilters } from 'src/app/shared/user/user-map-workspace-settings';
import { UserSettingsStreamService } from 'src/app/shared/user/user-settings-stream.service';

import { JobOp } from 'src/app/shared/common/enums/list-panel/job-filter.enum';
import { Page, PageSize } from 'src/app/shared/common/enums/list-panel/pagination.enum';
import { PanelViewMode } from 'src/app/shared/common/enums/list-panel/panel-view-mode.enum';
import { JobFeatureService } from 'src/app/shared/map-data-services/feature/job-feature-cache.service';

@Component({
    selector: 'survey-job-panel',
    templateUrl: 'survey-job-panel.component.html',
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [style({ opacity: 0 }), animate('100ms ease-in', style({ opacity: 1 }))]),
            transition(':leave', [animate('100ms ease-out', style({ opacity: 0 }))])
        ]),
        trigger('slideOut', [
            transition(':enter', [
                style({ height: 0, opacity: 0, transform: 'translateY(-16px)' }),
                animate('200ms ease-out', style({ height: '*', opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ height: 0, opacity: 0, transform: 'translateY(-16px)' }))
            ])
        ])
    ]
})
export class SurveyJobPanelComponent implements OnInit, OnDestroy {
    @ViewChild('secondaryPanel', { static: false }) secondaryPanel: ElementRef;
    @ViewChild('surveyJobSortMenu') sortMenu: any; // Reference to the sort-menu component

    constructor(
        private menuService: MenuService,
        private surveyJobService: SurveyJobService,
        private messaging: MessagingService,
        private translate: TranslationService,
        private route: ActivatedRoute,
        private projectStream: ProjectStreamService,
        private userSettingsStream: UserSettingsStreamService,
        private jobFeatureService: JobFeatureService,
        private jobFilterStreamsService: JobFilterStreamsService,
        private panelMenuService: PanelMenuService,
        private itemSortService: ItemSortService,
        private jobPreloadService: SurveyJobPreloadService,
        private surveyJobStreams: SurveyJobStreamsService
    ) {}

    public loading = true;
    public surveyJobs: SurveyJob[] = [];
    public selectedSurveyJob: SurveyJob;
    public currentProjectUsers: User[] = [];
    public connectTags: string[] = [];
    public currentScrollPosition: number;

    // Filters
    public filterAppliedCount = 0;
    public filterFields: FilterField[] = [];
    public surveyFilterValues: SurveyFilterValues;

    // Tools bar
    public showSearch = false;
    public showFilterPanel = false;
    public showSortPanel = false;
    public showFilterApplied = false;
    public sortApplied = false;
    public viewMode: PanelViewMode = PanelViewMode.List;

    //visibility state
    public visibilityState = {
        isCurrentlyVisibleAllJobs: true, // Track visibility toggle state UI level
        isHideAllJobs: false // Track visibility toggle state at user settings level
    };

    // Panel header action items
    public headerActions: ActionItem[] = [];

    // Sort menu configuration
    // public sortMenuItems: SortMenuItem<SurveyJob>[] = [];
    // public selectedSortOption: string = '';

    public sortMenuItems: SortMenuItem<SurveyJob>[] = this.itemSortService.getSortMenuItems<SurveyJob>();
    public selectedSortOption: string = String(ItemSortMode.DATE_MODIFIED); // Default sort option

    public currentSortDirection: 'asc' | 'desc' = 'desc';

    // Tabs
    public tabIndex = 0;
    private activeTabs = [SurveyJobStatus.New, SurveyJobStatus.InProgress, SurveyJobStatus.FieldWorkComplete];
    private completeTabs = [SurveyJobStatus.Closed];
    public tabs = [
        { label: this.translate.instant('TC.Common.Active'), value: this.jobTabValue(this.activeTabs) },
        {
            label: this.translate.instant('TC.Common.Status.Closed'),
            value: this.jobTabValue(this.completeTabs)
        }
    ];

    // Pagination - see SurveyJobListOptions for more details
    private pagination = {
        page: Page.First,
        pageSize: PageSize.Large,
        totalItems: 0
    };

    private statusCode: HttpStatusCode;
    private loadNextPage$ = new Subject<void>();
    public allItemsLoaded = false;

    // Search
    public searchInput: string = '';
    public onSearchInputChangeDebounce = _.debounce(this.onSearchInputChange, 500);

    // exposed enums
    public PanelViewMode = PanelViewMode;
    public ButtonType = ButtonType;

    private destroyed = new Subject<void>();

    async ngOnInit(): Promise<void> {
        this.checkProjectChange();
        this.initializeSortState();
        this.initHeaderActions();
        this.updateHeaderIcon();
        await this.initializeFilters();
        this.setInitialSurveyFilterValuesFromPreviousFilter();

        // Now setup route change subscription
        this.route.queryParamMap.pipe(takeUntil(this.destroyed)).subscribe(() => {
            // This will make sure things will be reactive during url change
            this.onTabChange(this.tabIndex);
        });

        // Debounce infinite scroll triggers
        this.loadNextPage$.pipe(debounceTime(500), takeUntil(this.destroyed)).subscribe(() => {
            if (!this.loading && !this.allItemsLoaded && this.statusCode === HttpStatusCode.PartialContent) {
                this.loadSurveyJobs(true);
            }
        });

        // Listen to reload stream to refresh the job list
        this.jobPreloadService.reloadJobListStream.pipe(takeUntil(this.destroyed)).subscribe(reload => {
            if (reload) {
                this.surveyJobs = [];
                this.loading = true;
                this.allItemsLoaded = false;
                this.pagination.page = 0; // reset pagination from the start
                this.pagination.totalItems = 0;
                this.loadSurveyJobs();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroyed.next(null);
    }

    onTabChange(index: number) {
        this.surveyJobs = [];

        this.tabIndex = index;
        this.allItemsLoaded = false;
        this.loading = true;
        this.pagination.page = 0; // reset pagination from the start

        const surveyFilters = this.jobFilterStreamsService.convertSurveyFiltersForUserSettings(this.surveyFilterValues);
        this.generateFilterExpression(surveyFilters);
        this.loadSurveyJobs();
    }

    onLoadNextPage(): void {
        this.loadNextPage$.next();
    }

    public closePanel(): void {
        this.menuService.closeActiveMenus();
    }

    // #region Panel Header related methods
    /**
     * Handles actions from the panel header
     */
    public handleAction(action: { item: ActionItem; event: MouseEvent }): void {
        const actionItem: ActionItem = action.item;
        switch (actionItem.id) {
            case 'search':
                this.toggleSearch();
                break;
            case 'filter':
                this.toggleFilterPanel();
                break;
            case 'sort':
                this.toggleSortMenu(action.event);
                break;
            case 'add':
                this.openCreateJobPanel();
                break;
            case 'visibility':
                this.toggleVisibility();
                break;
            default:
                console.log('Unknown action:', actionItem.id);
        }
    }

    /**
     * Toggles search visibility and updates header action state
     */
    public toggleSearch(): void {
        this.showSearch = !this.showSearch;
        this.updateHeaderActionState('search', this.showSearch);
    }

    /**
     * Toggles filter panel visibility and updates header action state
     */
    public toggleFilterPanel(): void {
        this.showFilterPanel = !this.showFilterPanel;
        this.updateHeaderActionState('filter', this.showFilterPanel);
    }

    /**
     * Toggles visibility icon and updates header action state
     */
    public toggleVisibility(): void {
        const settings = this.userSettingsStream.getCurrentMapWorkspaceSettings();

        if (settings.hideAllJobs) {
            // Switch to "Show All" mode
            settings.hideAllJobs = false;
            settings.visibleJobIds = [];
            settings.hiddenJobIds = [];
            this.visibilityState = {
                isCurrentlyVisibleAllJobs: true,
                isHideAllJobs: false
            };
            this.surveyJobStreams.deferVisibleAllJobsStream(true);
        } else {
            // Switch to "Hide All" mode
            settings.hideAllJobs = true;
            settings.hiddenJobIds = [];
            settings.visibleJobIds = [];
            this.visibilityState = {
                isCurrentlyVisibleAllJobs: false,
                isHideAllJobs: true
            };
            this.surveyJobStreams.deferVisibleAllJobsStream(false);
        }

        // Sync local job visibility state
        this.surveyJobStreams.setLocalJobVisibilityState({
            hideAllJobs: this.visibilityState.isHideAllJobs,
            visibleJobIds: settings.visibleJobIds,
            hiddenJobIds: settings.hiddenJobIds
        });

        this.userSettingsStream.updateCurrentWorkspaceSettings(settings);
        this.applyVisibilityToLoadedJobs();
        this.panelMenuService.updateVisibilityIcon(this.headerActions, this.visibilityState.isCurrentlyVisibleAllJobs);
    }

    /**
     * Opens the sort menu context menu
     */
    public toggleSortMenu(event?: MouseEvent): void {
        if (this.sortMenu && event) {
            this.showSortPanel = !this.showSortPanel;
            // Use the event target as the anchor for the context menu if provided
            if (this.showSortPanel) this.sortMenu.openContextMenu(event);

            this.updateHeaderActionState('sort', this.showSortPanel);
        }
    }

    // Toggles the create new job panel
    public openCreateJobPanel(): void {
        this.viewMode = this.viewMode === PanelViewMode.New ? PanelViewMode.List : PanelViewMode.New;
        this.updateHeaderActionState('add', this.viewMode === PanelViewMode.New);
    }

    /**
     * Handles the sort menu closed event
     */
    public onSortMenuClosed(): void {
        // Only update the header action state if sorting is not applied
        // Because this onSortMenuClosed() gets called on sort menu close and while selecting the sort option
        if (this.showSortPanel) {
            this.showSortPanel = false;
            this.updateHeaderActionState('sort', false);
        }
    }

    /**
     * Updates the active state of a header action
     */
    private updateHeaderActionState(actionId: string, isActive: boolean): void {
        this.panelMenuService.updateActionState(this.headerActions, actionId, isActive);
    }

    /**
     * Initializes the header action buttons
     */
    private initHeaderActions(): void {
        this.headerActions = this.panelMenuService.getSurveyJobPanelActions(
            this.showSearch,
            this.showFilterPanel,
            this.showSortPanel,
            this.visibilityState.isCurrentlyVisibleAllJobs
        );
    }

    // #endregion

    // -------------------------------
    // Search related methods
    onSearchInputChange(searchInput: string): void {
        this.searchInput = searchInput;
        this.loading = true;
        this.allItemsLoaded = false;
        // Load survey jobs with searchInput updated model above
        this.loadSurveyJobs();
    }

    public get searchPlaceholder(): string {
        if (this.tabIndex === 0) {
            return this.translate.instant('Survey.SurveyJobPanel.Search.ActiveJobs');
        } else {
            return this.translate.instant('Survey.SurveyJobPanel.Search.ClosedJobs');
        }
    }

    // #region Load SurveyJobs

    // -------------------------------
    // List Panel related methods
    private loadSurveyJobs(paginate = false): void {
        this.allItemsLoaded = false;

        // update pagination if we are paginating
        this.pagination.page = paginate ? this.pagination.page + 1 : 0;

        const options = this.jobListSettings(this.pagination.page);

        this.surveyJobService
            .getSurveyJobs(options)
            .pipe(takeUntil(this.destroyed))
            .subscribe({
                next: response => this.handleJobResponse(response, paginate),
                error: error => this.handleJobError(error, paginate)
            });
    }
    // #endregion

    public selectSurveyJob({ job, scrollPosition }: { job: SurveyJob; scrollPosition: number }): void {
        this.selectedSurveyJob = job;
        if (!job.visible) {
            job.visible = true;
            this.onJobVisibilityToggle(job);
        }
        this.currentScrollPosition = scrollPosition;
        this.viewMode = PanelViewMode.Detail;
    }

    public onJobVisibilityToggle(job: SurveyJob): void {
        const settings = this.userSettingsStream.getCurrentMapWorkspaceSettings();

        if (this.visibilityState.isHideAllJobs) {
            // "Hide All" mode: manage visibleJobIds
            if (job.visible) {
                if (!settings.visibleJobIds.includes(job.id)) {
                    // Create new array instead of mutating to ensure change detection works
                    settings.visibleJobIds = [...settings.visibleJobIds, job.id];
                    this.surveyJobStreams.deferVisibleJobsStream$.next([job]);
                }
            } else {
                settings.visibleJobIds = settings.visibleJobIds.filter(id => id !== job.id);
                this.surveyJobStreams.deferHideJobsStream$.next([job]);
            }
            this.visibilityState.isCurrentlyVisibleAllJobs = false; // Icon stays "hide all"
        } else {
            // "Show All" mode: manage hiddenJobIds
            if (job.visible) {
                settings.hiddenJobIds = settings.hiddenJobIds.filter(id => id !== job.id);
                this.surveyJobStreams.deferVisibleJobsStream$.next([job]);
            } else {
                if (!settings.hiddenJobIds.includes(job.id)) {
                    // Create new array instead of mutating to ensure change detection works
                    settings.hiddenJobIds = [...settings.hiddenJobIds, job.id];
                    this.surveyJobStreams.deferHideJobsStream$.next([job]);
                }
            }
            // Icon changes based on hidden count
            this.visibilityState.isCurrentlyVisibleAllJobs = settings.hiddenJobIds.length === 0;
        }
        this.surveyJobStreams.setLocalJobVisibilityState({
            hideAllJobs: this.visibilityState.isHideAllJobs,
            visibleJobIds: settings.visibleJobIds,
            hiddenJobIds: settings.hiddenJobIds
        });
        this.userSettingsStream.updateCurrentWorkspaceSettings(settings);
        this.panelMenuService.updateVisibilityIcon(this.headerActions, this.visibilityState.isCurrentlyVisibleAllJobs);
    }

    public navigateTo(viewMode: PanelViewMode): void {
        this.viewMode = viewMode;
    }

    public goToList(viewMode: PanelViewMode): void {
        this.navigateTo(viewMode);
        this.updateHeaderActionState('add', false);
    }

    private async initializeFilters(): Promise<void> {
        this.currentProjectUsers = await this.projectStream.getCurrentProjectUsers();
        this.connectTags = await this.projectStream.getProjectTags().then(tags => tags.map(tag => tag.label));
        this.filterFields = [
            {
                id: SurveyJobFilterId.createdUsers,
                label: this.translate.instant('Survey.SurveyJobPanel.Filter.CreatedBy'),
                type: 'user',
                placeholder: this.translate.instant('TC.Common.SearchEllipsis'),
                users: this.currentProjectUsers
            },
            {
                id: SurveyJobFilterId.assignedUsers,
                label: this.translate.instant('Survey.SurveyJobPanel.Filter.AssignedTo'),
                type: 'user',
                placeholder: this.translate.instant('TC.Common.SearchEllipsis'),
                users: this.currentProjectUsers
            },
            {
                type: 'group',
                label: this.translate.instant('Survey.SurveyJobPanel.Filter.LastUpdatedDate'),
                fields: [
                    {
                        id: SurveyJobFilterId.updatedFromDate,
                        placeholder: this.translate.instant('TC.Common.From'),
                        type: 'date'
                    },
                    {
                        id: SurveyJobFilterId.updatedToDate,
                        placeholder: this.translate.instant('TC.Common.To'),
                        type: 'date'
                    }
                ]
            },
            {
                type: 'group',
                label: this.translate.instant('Survey.SurveyJobPanel.Filter.CreatedDate'),
                fields: [
                    {
                        id: SurveyJobFilterId.createdFromDate,
                        placeholder: this.translate.instant('TC.Common.From'),
                        type: 'date'
                    },
                    {
                        id: SurveyJobFilterId.createdToDate,
                        placeholder: this.translate.instant('TC.Common.To'),
                        type: 'date'
                    }
                ]
            },
            {
                id: SurveyJobFilterId.status,
                label: this.translate.instant('Common.Status'),
                type: 'dropdown',
                idField: 'id',
                textField: 'text',
                placeholder: this.translate.instant('Common.SelectStatus'),
                options: Object.keys(statusDisplayNameToValueMap),
                multiple: true
            },
            {
                id: SurveyJobFilterId.tags,
                label: this.translate.instant('TCW_Tags'),
                type: 'chipFilter',
                placeholder: this.translate.instant('TC.Common.SearchEllipsis'),
                multiInputMode: true,
                options: this.connectTags
            }
        ];
    }

    private setInitialSurveyFilterValuesFromPreviousFilter(): void {
        const previousFilter: UserMapWorkspaceSurveyFilters =
            this.userSettingsStream.getCurrentMapWorkspaceSettings()?.surveyFilters ||
            (JobFilterStreamsService.getDefaultValues(this.filterFields) as UserMapWorkspaceSurveyFilters);
        this.surveyFilterValues = this.jobFilterStreamsService.getInitialSurveyFilterValues(
            previousFilter,
            this.currentProjectUsers
        );
        this.generateFilterExpression(previousFilter);
        this.filterAppliedCount = JobFilterStreamsService.filterAppliedCount<SurveyFilterValues>(
            this.surveyFilterValues
        );
    }

    public onFilterPanelChange(event: FilterSurveyJobChange) {
        // Reset the state of jobs while doing filtering
        this.surveyJobs = [];
        this.allItemsLoaded = false;
        this.loading = true;

        // Update only the changed field, or all if clear
        if (event.fieldChanged) {
            this.surveyFilterValues[event.fieldChanged] = event.value[event.fieldChanged];
        } else if (event.clear) {
            // If clear or initial load, update all
            this.surveyFilterValues = { ...event.value }; // Reset to default values
        }
        this.filterAppliedCount = event.filterAppliedCount;
        const surveyFilters = this.jobFilterStreamsService.convertSurveyFiltersForUserSettings(this.surveyFilterValues);
        // Save filter state to user settings
        this.jobFilterStreamsService.saveSurveyFiltersToUserSettings(surveyFilters);
        this.generateFilterExpression(surveyFilters);
        this.loadSurveyJobs();
    }

    /**
     * Handles sort change events from the sort menu
     */
    public onSortChange(event: SortChangeEvent<SurveyJob>): void {
        // Reset the state of jobs while sorting
        this.surveyJobs = [];
        this.allItemsLoaded = false;
        this.loading = true;

        // Update the sort state
        this.selectedSortOption = event.sortMode.id;
        this.currentSortDirection = event.direction;
        this.sortApplied = true;

        // Update the service with the new sort state
        this.panelMenuService.updateSortState(this.selectedSortOption, this.currentSortDirection);

        // Update the header action to show it's active
        this.updateHeaderActionState('sort', true);

        // Reload the survey jobs with the new sort
        this.loadSurveyJobs();
    }

    private initializeSortState(): void {
        if (!this.panelMenuService.currentSortState) {
            this.panelMenuService.updateSortState(this.selectedSortOption, this.currentSortDirection);
        } else {
            this.selectedSortOption = this.panelMenuService.currentSortState.sortMode;
            this.currentSortDirection = this.panelMenuService.currentSortState.direction;
            this.sortApplied = true;
        }
    }

    private generateFilterExpression(surveyFilters: UserMapWorkspaceSurveyFilters) {
        let allowedStatus = this.tabIndex === 0 ? this.activeTabs : this.completeTabs;
        // Generate filter expression string
        this.jobFilterStreamsService.buildFilterExpressionString<UserMapWorkspaceSurveyFilters>(
            surveyFilters,
            allowedStatus
        );
    }

    public clearFilter() {
        // If clear or initial load, update all
        this.onFilterPanelChange({
            clear: true,
            value: JobFilterStreamsService.getDefaultValues(this.filterFields) as SurveyFilterValues,
            fieldChanged: null,
            filterAppliedCount: 0
        });
    }

    private jobListSettings(page: number): Partial<SurveyJobListOptions> {
        const surveyFilterExpression = this.jobFilterStreamsService.filterExpressionString$.getValue();
        // Handle filter expression
        let filter: SurveyJobListOptions['filter'] = surveyFilterExpression;
        if (this.searchInput) {
            filter =
                filter && filter.trim() !== ''
                    ? `${filter} AND $Name ${JobOp.CN} '${StringUtils.escapeSql(this.searchInput.trim())}'`
                    : `$Name ${JobOp.CN} '${StringUtils.escapeSql(this.searchInput.trim())}'`;
        }

        const supportedFields = [
            JobListSupportedFields.AssigneeName,
            JobListSupportedFields.Tags,
            JobListSupportedFields.Status
        ];
        const fields: SurveyJobListOptions['fields'] = Object.values(supportedFields).join(',');

        // Handle sort options
        let sortBy: SurveyJobListOptions['sortBy'] = null;
        if (this.sortApplied && this.selectedSortOption) {
            // Map the sort menu option to the actual field name in the API
            // Based on the structure of SurveyJobSortOrder, we need to use one of the SurveyJobSupportedFields
            let apiSortField: string;

            switch (Number(this.selectedSortOption)) {
                case ItemSortMode.NAME:
                    apiSortField = 'Name';
                    break;
                case ItemSortMode.DATE_CREATED:
                    apiSortField = 'CreatedUtc';
                    break;
                case ItemSortMode.DATE_MODIFIED:
                    apiSortField = 'UpdatedUtc';
                    break;
                default:
                    apiSortField = 'UpdatedUtc'; // Default sort field
            }

            const direction = this.currentSortDirection === 'asc' ? SortDirection.ASC : SortDirection.DESC;
            sortBy = `${apiSortField} ${direction}` as SurveyJobListOptions['sortBy'];
        }

        return {
            filter,
            sortBy,
            fields,
            pagination: {
                page,
                pageSize: this.pagination.pageSize || PageSize.Large,
                totalItems: this.pagination.totalItems || 0
            }
        };
    }

    private handleJobResponse(
        response: { items: SurveyJob[]; total: number; statusCode?: number },
        paginate: boolean
    ): void {
        const jobs = response.items || [];
        this.onVisibleJobsChanged(jobs);

        // Apply visibility settings from user preferences
        this.applyVisibilityFromUserSettings(jobs);

        if (paginate) {
            this.surveyJobs = [...this.surveyJobs, ...jobs];
        } else {
            this.surveyJobs = jobs;
        }

        this.pagination.totalItems = response?.total ?? this.surveyJobs.length;

        // If fewer than pageSize returned, or we've loaded all items, stop loading
        this.allItemsLoaded =
            jobs.length < this.pagination.pageSize || this.surveyJobs.length >= this.pagination.totalItems;

        // track httpStatusCode for pagination = validation
        this.statusCode = response.statusCode;
        this.loading = false;
    }

    private handleJobError(error: any, paginate: boolean): void {
        if (paginate) {
            this.allItemsLoaded = true;
        } else {
            this.messaging.showError(this.translate.instant('Survey.SurveyJobPanel.ErrorLoadMessage'));
            this.surveyJobs = [];
            this.loading = false;
        }
    }

    // -------------------------------
    // Visibility persistence methods

    /**
     * Apply visibility settings from user preferences to loaded jobs
     * Called when jobs are loaded from API (handles pagination)
     */
    private applyVisibilityFromUserSettings(jobs: SurveyJob[]): SurveyJob[] {
        const settings = this.userSettingsStream.getCurrentMapWorkspaceSettings();

        jobs.forEach(job => {
            if (settings.hideAllJobs) {
                // In "Hide All" mode: only visible if in exception list
                job.visible = settings.visibleJobIds?.includes(job.id) || false;
            } else {
                // In "Show All" mode: visible unless in hidden list
                job.visible = !settings.hiddenJobIds?.includes(job.id);
            }
        });
        return jobs;
    }

    /**
     * Update the header visibility icon based on current settings
     */
    private updateHeaderIcon(): void {
        const { hideAllJobs, hiddenJobIds } = this.surveyJobStreams.getLocalJobVisibilityState();

        if (hideAllJobs) {
            // In "Hide All" mode: always show hide icon
            this.visibilityState = {
                isCurrentlyVisibleAllJobs: false,
                isHideAllJobs: true
            };
        } else {
            // In "Show All" mode: icon depends on whether any jobs are hidden
            this.visibilityState = {
                isCurrentlyVisibleAllJobs: !hiddenJobIds || hiddenJobIds.length === 0,
                isHideAllJobs: false
            };
        }

        this.panelMenuService.updateVisibilityIcon(this.headerActions, this.visibilityState.isCurrentlyVisibleAllJobs);
    }

    /**
     * Apply visibility settings to all currently loaded jobs
     * Called when global visibility toggle is changed
     */
    private applyVisibilityToLoadedJobs(): void {
        this.applyVisibilityFromUserSettings(this.surveyJobs);
    }

    // -------------------------------
    // Tab related methods
    private jobTabValue(status: SurveyJobStatus[]): string {
        if (!status || status.length === 0) {
            return null;
        }

        // if multiple, join them with comma
        if (status.length > 1) return status.map(s => `'${s}'`).join(', ');

        // single status, return as string
        return `'${status[0]}'`;
    }

    // #region Survey Job Preloading and Project Change

    /**
     * Triggers preloading when visible survey jobs change.
     * @param visibleJobs Array of currently visible SurveyJob items.
     */
    onVisibleJobsChanged(visibleJobs: SurveyJob[]): void {
        if (!visibleJobs?.length) return;

        const visibleJobsIdWithETag = visibleJobs.map(job => ({ id: job.id, eTag: job.eTag }));
        this.jobPreloadService.queueJobsForPreloading(visibleJobsIdWithETag);
    }

    /**
     * Clears the job cache when the project changes.
     */
    private checkProjectChange(): void {
        this.projectStream.currentProjectStream.pipe(takeUntil(this.destroyed)).subscribe(project => {
            if (project?.id !== this.jobPreloadService.currentProjectId) {
                this.jobPreloadService.clearAllCache();
                this.panelMenuService.clearSortState();
                this.jobFeatureService.clearCache();
                this.jobPreloadService.currentProjectId = project?.id;
            }
        });
    }

    // #endregion
    onJobUpdated(updatedJob: SurveyJob) {
        updatedJob.visible = true;
        // Update the job in the list if it exists
        const index = this.surveyJobs.findIndex(job => job.id === updatedJob.id);
        if (index !== -1) {
            this.surveyJobs[index] = updatedJob;
        }
    }
}
